/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { VersionsDropDownList } from 'src/languageStudio/appShell/prebuiltDemos/components/VersionsDropDownList';
import { TryoutModelVersion } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedModelVerion';

const messages = defineMessages({
    save: { id: 'languageStudio.apiAndModelVersionsModal.save', defaultMessage: 'Save' },
    here: { id: 'languageStudio.apiAndModelVersionsModal.here', defaultMessage: 'here.' },
    cancel: { id: 'languageStudio.apiAndModelVersionsModal.cancel', defaultMessage: 'Cancel' },
    apiVersion: { id: 'languageStudio.apiAndModelVersionsModal.apiVersion', defaultMessage: 'API Version' },
    modelVersion: { id: 'languageStudio.apiAndModelVersionsModal.modelVersion', defaultMessage: 'Model Version' },
    modalHeader: { id: 'languageStudio.apiAndModelVersionsModal.modalHeader', defaultMessage: 'Change {versionsType}' },
    moreInfo: { id: 'languageStudio.apiAndModelVersionsModal.moreInfo', defaultMessage: 'You can find more information' },
    apiAndModelVersions: { id: 'languageStudio.apiAndModelVersionsModal.apiAndModelVersions', defaultMessage: 'API & Model Versions' },
    modalDescription: {
        id: 'languageStudio.apiAndModelVersionsModal.modalDescription',
        defaultMessage: 'Choose which {versionsType} you will use for the try it out experience.'
    },
    selectVersionDescription: {
        id: 'languageStudio.apiAndModelVersionsModal.selectVersionDescription',
        defaultMessage: `Switch between the latest {type} available in preview, or our latest generally available stable version.`
    }
});

type ApiAndModelVersionsModalProps = BasicModalProps<{ name: string }> & {
    /**
     * Add learn more link section if provided
     */
    learnMoreLink?: string;

    /**
     * Add Api version drop down list if provided
     */
    apiVersion?: TryoutModelVersion;

    /**
     * Add Model version drop down list if provided
     */
    modelVersion?: TryoutModelVersion;

    apiVersionOptions?: IDropdownOption[];

    modelVersionOptions?: IDropdownOption[];

    handleApiVersionChange?: (optionKey: TryoutModelVersion) => void;

    handleModelVersionChange?: (optionKey: TryoutModelVersion) => void;
};

export const ApiAndModelVersionsModal = withModalMounter(
    observer((props: ApiAndModelVersionsModalProps) => {
        const {
            learnMoreLink,
            apiVersionOptions,
            apiVersion = null,
            modelVersion = null,
            modelVersionOptions,
            onDismiss,
            handleApiVersionChange,
            handleModelVersionChange
        } = props;

        const { localize } = useLocalization();

        const [chosenModelVersion, setChosenModelVersion] = React.useState<TryoutModelVersion>(modelVersion);
        const [chosenApiVersion, setChosenApiVersion] = React.useState<TryoutModelVersion>(apiVersion);

        const versionsType = localize(
            apiVersion && modelVersion ? messages.apiAndModelVersions : modelVersion ? messages.modelVersion : messages.apiVersion
        );

        const getVersionOptions = (isApiVersion: boolean): IDropdownOption[] => (isApiVersion ? apiVersionOptions : modelVersionOptions);

        const versionOptionsDropDown = (isApiVersion: boolean) => (
            <VersionsDropDownList
                versionName={localize(isApiVersion ? messages.apiVersion : messages.modelVersion)}
                versionOptions={getVersionOptions(isApiVersion)}
                chosenVersion={isApiVersion ? chosenApiVersion : chosenModelVersion}
                setVersion={isApiVersion ? setChosenApiVersion : setChosenModelVersion}
                toolTipContent={localize(messages.selectVersionDescription, {
                    type: localize(isApiVersion ? messages.apiVersion : messages.modelVersion)
                })}
            />
        );

        const handleSubmit = () => {
            handleApiVersionChange(chosenApiVersion);
            handleModelVersionChange(chosenModelVersion);
            onDismiss();
        };

        return (
            <BasicModal
                {...props}
                valid
                onSubmit={handleSubmit}
                doneButtonText={localize(messages.save)}
                dialogContentProps={{
                    title: localize(messages.modalHeader, { versionsType }),
                    styles: { innerContent: { height: '100%', overflow: 'unset' } }
                }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <LuisText>
                        {localize(messages.modalDescription, { versionsType })}&nbsp;
                        {learnMoreLink && localize(messages.moreInfo)}&nbsp;
                        {learnMoreLink && <ExternalLink href={learnMoreLink}>{localize(messages.here)}</ExternalLink>}
                    </LuisText>
                    {modelVersion && versionOptionsDropDown(false)}
                    {apiVersion && versionOptionsDropDown(true)}
                </Stack>
            </BasicModal>
        );
    })
);
